import { motion } from 'framer-motion';
import { Globe, Search, Shield, Ship, TrendingUp, CheckCircle, Factory, Award } from 'lucide-react';
import { Link } from 'react-router-dom';

const GlobalSourcing = () => {
  const services = [
    {
      icon: Search,
      title: 'Vendor Discovery',
      description: 'Access to our network of 50+ verified Chinese manufacturers across furniture, materials, and components.'
    },
    {
      icon: Shield,
      title: 'Quality Inspection',
      description: '100% factory inspection and quality verification before shipment to ensure international standards.'
    },
    {
      icon: Ship,
      title: 'Logistics Management',
      description: 'End-to-end shipping coordination from Chinese ports to Indian destinations with customs clearance.'
    },
    {
      icon: TrendingUp,
      title: 'Cost Optimization',
      description: 'Competitive pricing through established partnerships and volume procurement advantages.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Requirement Analysis',
      description: 'We understand your product specifications, quantity needs, and quality standards.'
    },
    {
      step: '02',
      title: 'Vendor Selection',
      description: 'Match your requirements with verified manufacturers from our trusted network.'
    },
    {
      step: '03',
      title: 'Sample Approval',
      description: 'Coordinate sample production and shipping for your evaluation and approval.'
    },
    {
      step: '04',
      title: 'Factory Inspection',
      description: 'On-site quality checks and production monitoring at Chinese manufacturing facilities.'
    },
    {
      step: '05',
      title: 'Order Management',
      description: 'Production timeline coordination and regular status updates throughout manufacturing.'
    },
    {
      step: '06',
      title: 'Quality Control',
      description: 'Pre-shipment inspection with detailed quality reports and photographic documentation.'
    },
    {
      step: '07',
      title: 'Shipping & Logistics',
      description: 'Container booking, documentation, customs clearance, and delivery coordination.'
    },
    {
      step: '08',
      title: 'Delivery & Support',
      description: 'Final delivery to your location with post-delivery support and warranty management.'
    }
  ];

  const benefits = [
    { icon: Factory, title: '50+ Partner Factories', description: 'Verified manufacturing network' },
    { icon: Award, title: '10+ Years Experience', description: 'Proven China sourcing expertise' },
    { icon: CheckCircle, title: '100% Quality Inspected', description: 'Every shipment verified' },
    { icon: Ship, title: '24/7 Logistics Support', description: 'Real-time tracking and updates' }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="global-sourcing-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="sourcing-hero">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Global Sourcing Division</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6">
              China Sourcing.<br />
              Quality Assured.<br />
              Seamless Delivery.
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-muted-foreground max-w-3xl">
              Leverage our established Chinese manufacturing partnerships to source premium furniture, components, 
              and materials with rigorous quality control and efficient logistics.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 md:py-32" data-testid="sourcing-services">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Our Services</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight">
              Complete Sourcing Solutions
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-slate-200 p-8 transition-all duration-500 hover:shadow-xl hover:border-slate-300"
                data-testid={`sourcing-service-${index}`}
              >
                <service.icon className="w-10 h-10 text-bronze-500 mb-4" />
                <h3 className="text-xl font-medium mb-3">{service.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 md:py-32 bg-slate-900 text-white" data-testid="sourcing-process">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-bronze-500 mb-4">Our Process</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight">
              From Inquiry to Delivery
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="relative"
                data-testid={`sourcing-process-${index}`}
              >
                <div className="text-6xl font-bold text-bronze-500/20 mb-4">{item.step}</div>
                <h3 className="text-lg font-medium mb-2">{item.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Network */}
      <section className="py-24 md:py-32" data-testid="partnership-network">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Globe className="w-16 h-16 text-bronze-500 mb-6" />
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Trusted Network</p>
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
                Established China Partnerships
              </h2>
              <p className="text-base leading-relaxed text-muted-foreground mb-6">
                Over a decade of relationships with Chinese manufacturers gives us unparalleled access to quality 
                suppliers, competitive pricing, and reliable production capabilities.
              </p>
              <p className="text-base leading-relaxed text-muted-foreground mb-6">
                Our on-ground presence and regular factory visits ensure consistent quality standards and enable 
                swift problem resolution throughout the sourcing process.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center bg-slate-900 text-white hover:bg-slate-800 h-12 px-8 uppercase tracking-widest text-xs font-bold transition-all"
              >
                Discuss Your Requirements
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-6"
            >
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-slate-50 border border-slate-200 p-6" data-testid={`benefit-${index}`}>
                  <benefit.icon className="w-8 h-8 text-bronze-500 mb-3" />
                  <h3 className="text-lg font-medium mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quality Assurance */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="quality-assurance">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative h-96 lg:h-full"
            >
              <img 
                src="https://images.pexels.com/photos/10119312/pexels-photo-10119312.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2" 
                alt="Quality Inspection"
                className="w-full h-full object-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Shield className="w-16 h-16 text-bronze-500 mb-6" />
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
                Rigorous Quality Control
              </h2>
              <p className="text-base leading-relaxed text-muted-foreground mb-6">
                Every shipment undergoes comprehensive inspection before leaving China. Our quality team verifies 
                specifications, finishes, packaging, and compliance with your requirements.
              </p>
              <div className="space-y-4">
                {[
                  'Pre-production sample approval',
                  'In-process quality monitoring',
                  'Pre-shipment final inspection',
                  'Detailed quality reports with photos'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-bronze-500 flex-shrink-0 mt-1" />
                    <span className="text-muted-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-900 text-white" data-testid="sourcing-cta">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
              Ready to Start Sourcing from China?
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              Share your requirements and let our sourcing experts find the perfect manufacturing partners for your needs.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center bg-bronze-500 text-white hover:bg-bronze-600 h-12 px-8 uppercase tracking-widest text-xs font-bold transition-all"
            >
              Get Sourcing Consultation
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GlobalSourcing;
